import {
  Alert,
  Button,
  Group,
  Modal,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  ThemeIcon,
  VisuallyHidden,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconShieldCheck, IconShieldOff } from '@tabler/icons-react';
import { type FormEvent, type Ref, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi } from '../api.js';
import { ErrorAlert } from './ErrorAlert.js';
import { TotpQr, TotpSecret, useTotpQr } from './TotpEnrollment.js';
import classes from './TwoStepModals.module.css';

/**
 * Ayarlar ▸ Hesap: iki adımlı doğrulama isteğe bağlı (sahip 2026-11-01). Kişi kendisi açar ve
 * kapatır; ikisinde de parola yeniden istenir (CLAUDE.md "Onay": TOTP işlemlerinin parolası
 * değişmez). Kurtarma kodu yok: telefonu kaybedenin doğrulamasını şirket yöneticisi kapatır.
 */

const failureCode = (failure: unknown) => (failure instanceof ApiError ? failure.code : 'GENERIC');

/**
 * Sunucudaki durum karttakinden farklı (başka sekmede ya da cihazda değişti, oturum kapandı):
 * `me` yenilenir, pencere kapanınca kart gerçek durumu gösterir.
 */
const STALE = new Set([
  'TOTP_ALREADY_ENABLED',
  'TOTP_NOT_ENROLLED',
  'TOTP_UNAVAILABLE',
  'UNAUTHENTICATED',
]);

/**
 * Hatadan sonra odak düzeltilecek alana döner. İstek sürerken alan ve düğme devre dışıdır;
 * Chromium odağı pencereden body'ye düşürür, klavyeyle yeniden yazılan kod kutulara girmezdi.
 */
function useFocusAfterError(busy: boolean) {
  const password = useRef<HTMLInputElement>(null);
  const code = useRef<HTMLInputElement>(null);
  const target = useRef<'password' | 'code' | null>(null);
  useEffect(() => {
    if (busy || target.current === null) return;
    const field = target.current;
    target.current = null;
    if (field === 'code') {
      code.current?.focus();
    } else {
      // Yanlış parola seçili: yazılan yenisi onun yerine geçer
      password.current?.focus();
      password.current?.select();
    }
  }, [busy]);
  return {
    password,
    code,
    after: (field: 'password' | 'code') => {
      target.current = field;
    },
  };
}

/**
 * Altı haneli kod alanı. Görünen etiket kutu grubunun adıdır (`aria-labelledby`; WCAG 2.5.3),
 * her hanenin kendi adı var ("Digit 1 of 6"). `ref` ilk haneye bağlanır.
 */
function CodeField({
  label,
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
  ref,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  ref?: Ref<HTMLInputElement>;
}) {
  const { t } = useTranslation();
  const id = `two-step-code-${useId().replace(/:/g, '')}`;
  return (
    <div>
      <Text
        component="label"
        htmlFor={`${id}-1`}
        id={`${id}-label`}
        size="sm"
        fw={500}
        mb={8}
        display="block"
      >
        {label}
      </Text>
      <PinInput
        {...(ref ? { ref } : {})}
        id={id}
        aria-labelledby={`${id}-label`}
        length={6}
        type="number"
        oneTimeCode
        size="md"
        classNames={{ root: classes.code, pinInput: classes.codeBox }}
        value={value}
        onChange={onChange}
        {...(onComplete ? { onComplete } : {})}
        {...(disabled ? { disabled } : {})}
        {...(autoFocus ? { autoFocus } : {})}
        getInputProps={(index) => ({ 'aria-label': t('totpSetup.digit', { n: index + 1 }) })}
      />
    </div>
  );
}

/**
 * Açmanın adımları: düğme değil sıralı liste, etkin adım `aria-current="step"` (ADR 0069 §9;
 * HseStepper ve DefectStepper ile aynı kalıp). Dar pencerede yazı alt satıra iner, yana kaymaz.
 */
function EnableSteps({ step }: { step: number }) {
  const { t } = useTranslation();
  const labels = [t('twoStep.steps.password'), t('twoStep.steps.app')];
  return (
    <ol className={classes.steps} aria-label={t('twoStep.steps.label')}>
      {labels.map((label, index) => {
        const state = index < step ? 'done' : index === step ? 'current' : 'next';
        return (
          <li
            key={label}
            className={classes.step}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className={classes.stepNo} aria-hidden>
              {state === 'done' ? <IconCheck size={14} stroke={2.4} /> : index + 1}
            </span>
            <span className={classes.stepLabel}>
              {label}
              {state === 'done' ? (
                <VisuallyHidden>{`, ${t('twoStep.steps.completed')}`}</VisuallyHidden>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

interface EnableProps {
  opened: boolean;
  onClose: () => void;
  /** Kod doğrulandı: oturumdaki `me` yenilenir. */
  onEnabled: () => Promise<unknown>;
  /** Sunucunun durumu karttakinden farklı çıktı: `me` yenilenir (`STALE`). */
  onStale: () => Promise<unknown>;
}

/** "Turn on": 1) parolayı onayla, 2) uygulamayı bağla ve kodu gir, sonra başarı bildirimi. */
export function EnableTwoStepModal({ opened, onClose, onEnabled, onStale }: EnableProps) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('twoStep.enableTitle')}
      closeButtonProps={{ 'aria-label': t('twoStep.close') }}
      centered
      size={460}
    >
      {/* Pencere kapanınca içerik söner: her açılış birinci adımdan başlar */}
      <EnableFlow onClose={onClose} onEnabled={onEnabled} onStale={onStale} />
    </Modal>
  );
}

function EnableFlow({ onClose, onEnabled, onStale }: Omit<EnableProps, 'opened'>) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const qr = useTotpQr(otpauthUri);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const focus = useFocusAfterError(busy);
  const done = useRef<HTMLButtonElement>(null);
  // Adım değişince tıklanan düğme söner: odak pencerede kalsın (bildirim adımında "Done")
  useEffect(() => {
    if (step === 2) done.current?.focus();
  }, [step]);

  const failed = (failure: unknown, field: 'password' | 'code') => {
    const failedCode = failureCode(failure);
    setError(failedCode);
    if (STALE.has(failedCode)) void onStale().catch(() => undefined);
    focus.after(field);
  };

  const enroll = async (event: FormEvent) => {
    event.preventDefault();
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      const enrollment = await officeApi.enrollTotp(password);
      setSecret(enrollment.secret);
      setOtpauthUri(enrollment.otpauthUri);
      setPassword('');
      setStep(1);
    } catch (failure) {
      failed(failure, 'password');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (value: string) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await officeApi.confirmTotp(value);
      // Profil okunamasa da doğrulama açıldı; rozet sonraki okumada düzelir
      await onEnabled().catch(() => undefined);
      setStep(2);
    } catch (failure) {
      setCode('');
      failed(failure, 'code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="lg">
      <EnableSteps step={step} />

      {step === 0 ? (
        <form onSubmit={(event) => void enroll(event)} noValidate>
          <Stack gap="md">
            <Text fz="sm" c="dimmed">
              {t('twoStep.passwordText')}
            </Text>
            <ErrorAlert code={error} />
            <PasswordInput
              ref={focus.password}
              label={t('twoStep.password')}
              autoComplete="current-password"
              data-autofocus
              required
              withAsterisk={false}
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={onClose} disabled={busy}>
                {t('twoStep.cancel')}
              </Button>
              <Button type="submit" loading={busy} disabled={!password}>
                {t('twoStep.continue')}
              </Button>
            </Group>
          </Stack>
        </form>
      ) : null}

      {step === 1 ? (
        <Stack gap="md">
          <Text fz="sm" c="dimmed">
            {t('twoStep.scan')}
          </Text>
          <ErrorAlert code={error} />
          <TotpQr qr={qr} />
          {secret ? <TotpSecret secret={secret} /> : null}
          <CodeField
            ref={focus.code}
            label={t('totpSetup.code')}
            value={code}
            onChange={setCode}
            onComplete={(value) => void confirm(value)}
            disabled={busy}
            autoFocus
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={busy}>
              {t('twoStep.cancel')}
            </Button>
            <Button
              leftSection={<IconShieldCheck size={16} />}
              disabled={code.length !== 6}
              loading={busy}
              onClick={() => void confirm(code)}
            >
              {t('twoStep.finish')}
            </Button>
          </Group>
        </Stack>
      ) : null}

      {step === 2 ? (
        <Stack gap="md" align="center" className={classes.done} role="status">
          <ThemeIcon size={56} radius="xl" color="teal" variant="light" aria-hidden>
            <IconShieldCheck size={30} stroke={1.8} />
          </ThemeIcon>
          <Text fw={600} fz="md" ta="center">
            {t('twoStep.enabled')}
          </Text>
          <Text fz="sm" c="dimmed" ta="center">
            {t('twoStep.enabledText')}
          </Text>
          <Button ref={done} onClick={onClose} fullWidth mt="xs">
            {t('twoStep.done')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

interface DisableProps {
  opened: boolean;
  onClose: () => void;
  /** Kapatıldı: oturumdaki `me` yenilenir, sayfa bildirimi gösterir. */
  onDisabled: () => Promise<unknown>;
  /** Sunucunun durumu karttakinden farklı çıktı: `me` yenilenir (`STALE`). */
  onStale: () => Promise<unknown>;
}

/** "Turn off": parola ve güncel kod; girişte yalnız parola sorulur, diğer oturumlar kapanır. */
export function DisableTwoStepModal({ opened, onClose, onDisabled, onStale }: DisableProps) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('twoStep.disableTitle')}
      closeButtonProps={{ 'aria-label': t('twoStep.close') }}
      centered
      size={460}
    >
      <DisableForm onClose={onClose} onDisabled={onDisabled} onStale={onStale} />
    </Modal>
  );
}

function DisableForm({ onClose, onDisabled, onStale }: Omit<DisableProps, 'opened'>) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const focus = useFocusAfterError(busy);
  const ready = password.length > 0 && code.length === 6;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await officeApi.disableTotp(password, code);
      await onDisabled().catch(() => undefined);
      onClose();
    } catch (failure) {
      const failed = failureCode(failure);
      setError(failed);
      if (STALE.has(failed)) void onStale().catch(() => undefined);
      // Yanlış kodda kod yeniden yazılır; yanlış parolada doğru kod korunur
      if (failed === 'INVALID_TOTP') setCode('');
      focus.after(failed === 'INVALID_TOTP' ? 'code' : 'password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        <Text fz="sm" c="dimmed">
          {t('twoStep.disableText')}
        </Text>
        {/* Sabit uyarı canlı bölge değil (role="note", depodaki kalıp); pencerede tek alert hata */}
        <Alert color="warning" variant="light" icon={<IconAlertTriangle size={18} />} role="note">
          <Text fz="sm">{t('twoStep.disableWarning')}</Text>
        </Alert>
        <ErrorAlert code={error} />
        <PasswordInput
          ref={focus.password}
          label={t('twoStep.password')}
          autoComplete="current-password"
          data-autofocus
          required
          withAsterisk={false}
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
        />
        <CodeField
          ref={focus.code}
          label={t('twoStep.code')}
          value={code}
          onChange={setCode}
          disabled={busy}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={busy}>
            {t('twoStep.cancel')}
          </Button>
          <Button
            type="submit"
            color="red"
            leftSection={<IconShieldOff size={16} />}
            loading={busy}
            disabled={!ready}
          >
            {t('twoStep.disable')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
